import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { QRCodeSVG } from 'qrcode.react';
import {
    Megaphone, Link2, QrCode, Save, CheckCircle, AlertCircle,
    Eye, Copy, ExternalLink, Users, TrendingUp, Settings,
    Building2, Phone, Mail, Star, ChevronRight, Edit3, X
} from 'lucide-react';
import { isAdminUser } from '../../utils/adminClaim';

function LabPromoSettings({ user }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [promoSettings, setPromoSettings] = useState(null);
    const [stats, setStats] = useState({ totalSignups: 0, pendingRequests: 0 });
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({
        description: '',
        strengths: [],
        contactPhone: '',
        contactEmail: ''
    });
    const [newStrength, setNewStrength] = useState('');

    // 허용된 기공소 ID (아이반치과기공소)
    const ALLOWED_LAB_ID = '76Y6wwTXUZcN7lbiHds3ZYSQcK92';
    const ALLOWED_EMAIL = 'ivanlab@naver.com';

    // 관리자 체크
    const isAdmin = isAdminUser(user);

    const isAllowed = user?.uid === ALLOWED_LAB_ID || user?.email === ALLOWED_EMAIL || isAdmin;
    const promoUrl = `https://www.dentalcluster.com/promo/ivan`;
    const promoCode = 'ivan';

    // 실제 프로모션 대상 기공소 ID (관리자도 이 기공소 설정을 수정)
    const targetLabId = ALLOWED_LAB_ID;

    useEffect(() => {
        if (user && isAllowed) {
            loadPromoSettings();
            loadStats();
        } else {
            setLoading(false);
        }
    }, [user, isAllowed]);

    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(''), 3000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

    useEffect(() => {
        if (errorMessage) {
            const t = setTimeout(() => setErrorMessage(''), 3000);
            return () => clearTimeout(t);
        }
    }, [errorMessage]);

    const loadPromoSettings = async () => {
        try {
            // 관리자는 대상 기공소 설정을 로드, 기공소 본인은 자신의 설정 로드
            const docId = isAdmin ? targetLabId : user.uid;
            const userDoc = await getDoc(doc(db, 'users', docId));
            if (userDoc.exists()) {
                const data = userDoc.data();
                const settings = data.promoSettings || {
                    enabled: true,
                    description: '신뢰할 수 있는 파트너, 아이반치과기공소입니다.\n최고의 품질과 서비스로 치과의 성공을 함께합니다.',
                    strengths: [
                        '20년 이상의 기공 경력',
                        '최신 CAD/CAM 장비 보유',
                        '빠르고 정확한 납품',
                        '철저한 품질 관리',
                        '친절한 상담 서비스'
                    ],
                    contactPhone: data.businessPhone || '',
                    contactEmail: data.email || ''
                };
                setPromoSettings(settings);
                setEditForm({
                    description: settings.description || '',
                    strengths: settings.strengths || [],
                    contactPhone: settings.contactPhone || '',
                    contactEmail: settings.contactEmail || ''
                });
            }
        } catch (error) {
            console.error('프로모션 설정 로드 실패:', error);
            setErrorMessage('설정을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const docId = isAdmin ? targetLabId : user.uid;

            // 프로모션을 통해 가입한 치과 수 조회
            const signupsQuery = query(
                collection(db, 'users'),
                where('promoSource', '==', 'ivan'),
                where('businessType', 'in', ['dental', 'clinic'])
            );
            const signupsSnap = await getDocs(signupsQuery);

            // 보류 중인 연결 요청 수 조회
            const pendingQuery = query(
                collection(db, 'connectionRequests'),
                where('fromLabId', '==', docId),
                where('status', '==', 'pending')
            );
            const pendingSnap = await getDocs(pendingQuery);

            setStats({
                totalSignups: signupsSnap.size,
                pendingRequests: pendingSnap.size
            });
        } catch (error) {
            console.error('통계 로드 실패:', error);
        }
    };

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            const docId = isAdmin ? targetLabId : user.uid;

            const promoData = {
                enabled: true,
                description: editForm.description,
                strengths: editForm.strengths,
                contactPhone: editForm.contactPhone,
                contactEmail: editForm.contactEmail,
                updatedAt: new Date().toISOString()
            };

            // 1. users 컬렉션에 저장
            await updateDoc(doc(db, 'users', docId), {
                promoSettings: promoData
            });

            // 2. promoPages 컬렉션에 저장 (공개 접근용)
            await setDoc(doc(db, 'promoPages', 'ivan'), {
                labId: docId,
                labName: '아이반치과기공소',
                ...promoData
            });

            setPromoSettings({
                ...promoSettings,
                ...editForm
            });
            setShowEditModal(false);
            setSuccessMessage('설정이 저장되었습니다.');
        } catch (error) {
            console.error('설정 저장 실패:', error);
            setErrorMessage('설정 저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    // 현재 설정을 공개 컬렉션에 동기화
    const handleSyncToPublic = async () => {
        setSaving(true);
        try {
            const docId = isAdmin ? targetLabId : user.uid;

            await setDoc(doc(db, 'promoPages', 'ivan'), {
                labId: docId,
                labName: '아이반치과기공소',
                enabled: true,
                description: promoSettings?.description || '신뢰할 수 있는 파트너, 아이반치과기공소입니다.\n최고의 품질과 서비스로 치과의 성공을 함께합니다.',
                strengths: promoSettings?.strengths || [
                    '10년 이상의 기공 경력',
                    '최신 CAD/CAM 장비 보유',
                    '빠르고 정확한 납품',
                    '철저한 품질 관리',
                    '친절한 상담 서비스'
                ],
                contactPhone: promoSettings?.contactPhone || '',
                contactEmail: promoSettings?.contactEmail || '',
                updatedAt: new Date().toISOString()
            });
            setSuccessMessage('랜딩페이지에 저장되었습니다!');
        } catch (error) {
            console.error('동기화 실패:', error);
            setErrorMessage('저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setSuccessMessage('복사되었습니다!');
    };

    const addStrength = () => {
        if (newStrength.trim() && editForm.strengths.length < 7) {
            setEditForm({
                ...editForm,
                strengths: [...editForm.strengths, newStrength.trim()]
            });
            setNewStrength('');
        }
    };

    const removeStrength = (index) => {
        setEditForm({
            ...editForm,
            strengths: editForm.strengths.filter((_, i) => i !== index)
        });
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div style={styles.spinner}></div>
                    <p style={styles.loadingText}>로딩 중...</p>
                </div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (!isAllowed) {
        return (
            <div style={styles.container}>
                <div style={styles.notAllowedCard}>
                    <AlertCircle size={48} color="#f59e0b" />
                    <h2 style={styles.notAllowedTitle}>접근 제한</h2>
                    <p style={styles.notAllowedText}>
                        이 기능은 특정 기공소에만 제공됩니다.<br />
                        프로모션 페이지 기능을 사용하려면 관리자에게 문의하세요.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {successMessage && (
                <div style={styles.successMsg}>
                    <CheckCircle size={18} /> {successMessage}
                </div>
            )}
            {errorMessage && (
                <div style={styles.errorMsg}>
                    <AlertCircle size={18} /> {errorMessage}
                </div>
            )}

            {/* 헤더 */}
            <div style={styles.header}>
                <div style={styles.headerIcon}>
                    <Megaphone size={28} color="#fff" />
                </div>
                <div>
                    <h1 style={styles.headerTitle}>프로모션 페이지 관리</h1>
                    <p style={styles.headerSubtitle}>치과 회원가입을 유도하는 광고 랜딩페이지를 관리합니다</p>
                </div>
            </div>

            {/* 통계 카드 */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <Users size={24} color="#6366f1" />
                    <div style={styles.statInfo}>
                        <span style={styles.statValue}>{stats.totalSignups}</span>
                        <span style={styles.statLabel}>프로모션 가입</span>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <TrendingUp size={24} color="#10b981" />
                    <div style={styles.statInfo}>
                        <span style={styles.statValue}>{stats.pendingRequests}</span>
                        <span style={styles.statLabel}>대기 요청</span>
                    </div>
                </div>
            </div>

            {/* QR 코드 및 링크 */}
            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <div style={styles.cardHeaderIcon}>
                        <QrCode size={20} color="#6366f1" />
                    </div>
                    <h2 style={styles.cardTitle}>프로모션 링크</h2>
                </div>
                <div style={styles.cardBody}>
                    <div style={styles.qrSection}>
                        <div style={styles.qrBox}>
                            <QRCodeSVG
                                value={promoUrl}
                                size={160}
                                level="H"
                                includeMargin={true}
                                style={{ borderRadius: 8 }}
                            />
                            <p style={styles.qrLabel}>QR 코드 스캔</p>
                        </div>
                        <div style={styles.linkSection}>
                            <div style={styles.linkBox}>
                                <Link2 size={18} color="#64748b" />
                                <span style={styles.linkText}>{promoUrl}</span>
                                <button
                                    onClick={() => copyToClipboard(promoUrl)}
                                    style={styles.copyBtn}
                                >
                                    <Copy size={16} />
                                </button>
                            </div>
                            <div style={styles.actionButtons}>
                                <button
                                    onClick={() => window.open(promoUrl, '_blank')}
                                    style={styles.previewBtn}
                                >
                                    <Eye size={16} />
                                    미리보기
                                </button>
                                <button
                                    onClick={() => copyToClipboard(promoUrl)}
                                    style={styles.shareBtn}
                                >
                                    <ExternalLink size={16} />
                                    링크 공유
                                </button>
                            </div>
                        </div>
                    </div>
                    <div style={styles.usageTip}>
                        <p style={styles.usageTipText}>
                            이 QR 코드와 링크를 치과 신문, 광고물 등에 활용하세요.<br />
                            가입한 치과에게 연결 요청을 직접 보내실 수 있습니다.
                        </p>
                    </div>
                </div>
            </div>

            {/* 랜딩페이지 설정 */}
            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <div style={styles.cardHeaderIcon}>
                        <Settings size={20} color="#8b5cf6" />
                    </div>
                    <h2 style={styles.cardTitle}>랜딩페이지 내용</h2>
                    <button
                        onClick={() => setShowEditModal(true)}
                        style={styles.editBtn}
                    >
                        <Edit3 size={16} />
                        수정
                    </button>
                    <button
                        onClick={handleSyncToPublic}
                        disabled={saving}
                        style={styles.syncBtn}
                    >
                        <Save size={16} />
                        {saving ? '저장중...' : '저장'}
                    </button>
                </div>
                <div style={styles.cardBody}>
                    <div style={styles.settingItem}>
                        <div style={styles.settingLabel}>
                            <Building2 size={18} color="#64748b" />
                            <span>기공소 소개</span>
                        </div>
                        <p style={styles.settingValue}>
                            {promoSettings?.description || '-'}
                        </p>
                    </div>

                    <div style={styles.settingItem}>
                        <div style={styles.settingLabel}>
                            <Star size={18} color="#64748b" />
                            <span>강점</span>
                        </div>
                        <div style={styles.strengthsList}>
                            {(promoSettings?.strengths || []).map((strength, idx) => (
                                <div key={idx} style={styles.strengthItem}>
                                    <CheckCircle size={16} color="#10b981" />
                                    <span>{strength}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={styles.settingRow}>
                        <div style={styles.settingItem}>
                            <div style={styles.settingLabel}>
                                <Phone size={18} color="#64748b" />
                                <span>연락처</span>
                            </div>
                            <p style={styles.settingValue}>
                                {promoSettings?.contactPhone || '-'}
                            </p>
                        </div>
                        <div style={styles.settingItem}>
                            <div style={styles.settingLabel}>
                                <Mail size={18} color="#64748b" />
                                <span>이메일</span>
                            </div>
                            <p style={styles.settingValue}>
                                {promoSettings?.contactEmail || '-'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 안내 */}
            <div style={styles.infoCard}>
                <h3 style={styles.infoTitle}>사용 방법</h3>
                <div style={styles.infoSteps}>
                    <div style={styles.infoStep}>
                        <div style={styles.stepNumber}>1</div>
                        <div style={styles.stepContent}>
                            <strong>QR 코드/링크 배포</strong>
                            <p>신문 광고, 명함, 홍보물에 QR 코드나 링크를 넣습니다</p>
                        </div>
                    </div>
                    <div style={styles.infoStep}>
                        <div style={styles.stepNumber}>2</div>
                        <div style={styles.stepContent}>
                            <strong>치과 회원가입</strong>
                            <p>치과가 링크를 통해 DentalCluster에 가입합니다</p>
                        </div>
                    </div>
                    <div style={styles.infoStep}>
                        <div style={styles.stepNumber}>3</div>
                        <div style={styles.stepContent}>
                            <strong>알림 수신</strong>
                            <p>새 가입 알림을 받고 해당 치과에 연결 요청을 보냅니다</p>
                        </div>
                    </div>
                    <div style={styles.infoStep}>
                        <div style={styles.stepNumber}>4</div>
                        <div style={styles.stepContent}>
                            <strong>연결 완료</strong>
                            <p>치과가 요청을 수락하면 주문 관계가 시작됩니다</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 수정 모달 */}
            {showEditModal && (
                <div style={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h3 style={styles.modalTitle}>랜딩페이지 내용 수정</h3>
                            <button onClick={() => setShowEditModal(false)} style={styles.closeBtn}>
                                <X size={24} color="#64748b" />
                            </button>
                        </div>
                        <div style={styles.modalBody}>
                            <div style={styles.inputGroup}>
                                <label style={styles.inputLabel}>기공소 소개</label>
                                <textarea
                                    value={editForm.description}
                                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                    placeholder="기공소를 소개하는 문구를 입력하세요"
                                    style={styles.textarea}
                                    rows={3}
                                />
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.inputLabel}>강점 (최대 7개)</label>
                                <div style={styles.strengthsEditor}>
                                    {editForm.strengths.map((strength, idx) => (
                                        <div key={idx} style={styles.strengthTag}>
                                            <span>{strength}</span>
                                            <button
                                                onClick={() => removeStrength(idx)}
                                                style={styles.removeTagBtn}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                {editForm.strengths.length < 7 && (
                                    <div style={styles.addStrengthRow}>
                                        <input
                                            type="text"
                                            value={newStrength}
                                            onChange={(e) => setNewStrength(e.target.value)}
                                            placeholder="새 강점 추가"
                                            style={styles.addStrengthInput}
                                            onKeyPress={(e) => e.key === 'Enter' && addStrength()}
                                        />
                                        <button onClick={addStrength} style={styles.addStrengthBtn}>
                                            추가
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div style={styles.inputRow}>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>연락처</label>
                                    <input
                                        type="tel"
                                        value={editForm.contactPhone}
                                        onChange={(e) => setEditForm({ ...editForm, contactPhone: e.target.value })}
                                        placeholder="연락처"
                                        style={styles.input}
                                    />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>이메일</label>
                                    <input
                                        type="email"
                                        value={editForm.contactEmail}
                                        onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })}
                                        placeholder="이메일"
                                        style={styles.input}
                                    />
                                </div>
                            </div>

                            <button
                                onClick={handleSaveSettings}
                                disabled={saving}
                                style={styles.saveButton}
                            >
                                {saving ? '저장 중...' : '저장'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#f1f5f9',
        padding: '24px',
        maxWidth: '800px',
        margin: '0 auto',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '4px solid #e2e8f0',
        borderTop: '4px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    loadingText: {
        marginTop: '16px',
        fontSize: '16px',
        color: '#64748b',
    },
    notAllowedCard: {
        backgroundColor: '#fff',
        borderRadius: '20px',
        padding: '48px 24px',
        textAlign: 'center',
        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    },
    notAllowedTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a',
        margin: '20px 0 12px',
    },
    notAllowedText: {
        fontSize: '15px',
        color: '#64748b',
        lineHeight: 1.6,
        margin: 0,
    },
    successMsg: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '16px 20px',
        backgroundColor: '#dcfce7',
        borderRadius: '12px',
        marginBottom: '20px',
        fontSize: '15px',
        fontWeight: '500',
        color: '#166534',
    },
    errorMsg: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '16px 20px',
        backgroundColor: '#fee2e2',
        borderRadius: '12px',
        marginBottom: '20px',
        fontSize: '15px',
        fontWeight: '500',
        color: '#991b1b',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px',
    },
    headerIcon: {
        width: '60px',
        height: '60px',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a',
        margin: '0 0 4px 0',
    },
    headerSubtitle: {
        fontSize: '14px',
        color: '#64748b',
        margin: 0,
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginBottom: '24px',
    },
    statCard: {
        backgroundColor: '#fff',
        borderRadius: '16px',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    },
    statInfo: {
        display: 'flex',
        flexDirection: 'column',
    },
    statValue: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#0f172a',
    },
    statLabel: {
        fontSize: '13px',
        color: '#64748b',
    },
    card: {
        backgroundColor: '#fff',
        borderRadius: '20px',
        marginBottom: '20px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '20px 24px',
        borderBottom: '1px solid #f1f5f9',
    },
    cardHeaderIcon: {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        backgroundColor: '#f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cardTitle: {
        flex: 1,
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
    },
    editBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#6366f1',
        cursor: 'pointer',
    },
    syncBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#10b981',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#fff',
        cursor: 'pointer',
        marginLeft: '8px',
    },
    cardBody: {
        padding: '24px',
    },
    qrSection: {
        display: 'flex',
        gap: '24px',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
    },
    qrBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '16px',
        backgroundColor: '#f8fafc',
        borderRadius: '16px',
    },
    qrLabel: {
        marginTop: '12px',
        fontSize: '13px',
        color: '#64748b',
        fontWeight: '500',
    },
    linkSection: {
        flex: 1,
        minWidth: '280px',
    },
    linkBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 16px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        marginBottom: '16px',
    },
    linkText: {
        flex: 1,
        fontSize: '14px',
        color: '#374151',
        wordBreak: 'break-all',
    },
    copyBtn: {
        padding: '8px',
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        cursor: 'pointer',
        color: '#64748b',
    },
    actionButtons: {
        display: 'flex',
        gap: '12px',
    },
    previewBtn: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px',
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#374151',
        cursor: 'pointer',
    },
    shareBtn: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#fff',
        cursor: 'pointer',
    },
    usageTip: {
        marginTop: '20px',
        padding: '16px',
        backgroundColor: '#eef2ff',
        borderRadius: '12px',
    },
    usageTipText: {
        fontSize: '14px',
        color: '#4f46e5',
        lineHeight: 1.6,
        margin: 0,
    },
    settingItem: {
        marginBottom: '20px',
    },
    settingLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#64748b',
        marginBottom: '8px',
    },
    settingValue: {
        fontSize: '15px',
        color: '#0f172a',
        margin: 0,
        lineHeight: 1.6,
        whiteSpace: 'pre-line',
    },
    settingRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
    },
    strengthsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    strengthItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '14px',
        color: '#374151',
    },
    infoCard: {
        backgroundColor: '#fff',
        borderRadius: '20px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    },
    infoTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a',
        margin: '0 0 20px 0',
    },
    infoSteps: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    infoStep: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
    },
    stepNumber: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: '#6366f1',
        color: '#fff',
        fontSize: '16px',
        fontWeight: '700',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    stepContent: {
        flex: 1,
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
    },
    modalContent: {
        backgroundColor: '#fff',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        borderBottom: '1px solid #f1f5f9',
        position: 'sticky',
        top: 0,
        backgroundColor: '#fff',
    },
    modalTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
    },
    closeBtn: {
        width: '44px',
        height: '44px',
        borderRadius: '10px',
        backgroundColor: '#f8fafc',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    modalBody: {
        padding: '24px',
    },
    inputGroup: {
        marginBottom: '20px',
    },
    inputLabel: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px',
    },
    input: {
        width: '100%',
        padding: '14px 16px',
        fontSize: '15px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        backgroundColor: '#f8fafc',
        outline: 'none',
        boxSizing: 'border-box',
    },
    textarea: {
        width: '100%',
        padding: '14px 16px',
        fontSize: '15px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        backgroundColor: '#f8fafc',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    inputRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
    },
    strengthsEditor: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '12px',
    },
    strengthTag: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        backgroundColor: '#dcfce7',
        borderRadius: '20px',
        fontSize: '13px',
        color: '#166534',
    },
    removeTagBtn: {
        padding: '2px',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#166534',
        display: 'flex',
        alignItems: 'center',
    },
    addStrengthRow: {
        display: 'flex',
        gap: '8px',
    },
    addStrengthInput: {
        flex: 1,
        padding: '10px 14px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        backgroundColor: '#f8fafc',
        outline: 'none',
    },
    addStrengthBtn: {
        padding: '10px 16px',
        backgroundColor: '#10b981',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#fff',
        cursor: 'pointer',
    },
    saveButton: {
        width: '100%',
        padding: '16px',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '12px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#fff',
        cursor: 'pointer',
        marginTop: '10px',
    },
};

export default LabPromoSettings;
